import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, Modal, Field, Button, inr, EmptyState } from "../components/UI";
import { TrendingDown, Wallet, AlertTriangle, Users, Plus, Upload, Phone, MessageCircle, Trash2, Receipt as ReceiptIcon } from "lucide-react";
import Typeahead from "../components/Typeahead";

const searchCustomersAsync = async (q) => {
  const url = q ? `/customers?q=${encodeURIComponent(q)}&limit=50` : "/customers?limit=50";
  const r = await api.get(url);
  return r.data;
};
import { toast } from "sonner";

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoMonth() { return new Date().toISOString().slice(0, 7); }

const KPICard = ({ icon: Icon, label, value, tone, testid }) => (
  <div className={`card-flat p-4 border-l-4 ${tone}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} className="text-[#94A3B8]"/>
      <span className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</span>
    </div>
    <div className="font-display font-bold text-2xl font-mono-num">{value}</div>
  </div>
);

export default function PaymentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [receiptsList, setReceiptsList] = useState([]);
  const fileRef = useRef(null);
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState("");
  const [form, setForm] = useState({
    customer_id: "", amount: "", receipt_date: isoToday(), bank_account: "Cash",
    method: "cash", voucher_no: "", note: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [d, c, r] = await Promise.all([
        api.get("/payments/dashboard"),
        api.get("/customers"),
        api.get(`/receipts?month=${isoMonth()}`),
      ]);
      setData(d.data); setCustomers(c.data); setReceiptsList(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addReceipt = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.amount) { toast.error("Customer + amount required"); return; }
    try {
      await api.post("/receipts", { ...form, amount: Number(form.amount) });
      toast.success("Receipt added — invoices auto-updated");
      setForm({ customer_id: "", amount: "", receipt_date: isoToday(), bank_account: "Cash", method: "cash", voucher_no: "", note: "" });
      setAddOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBusyUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/receipts/import-busy", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = r.data.job_id;
      if (!jobId) {
        // Backward compat
        const d = r.data;
        toast.success(`✓ Imported ${d.imported} receipts · ${d.skipped_existing} skipped · ${d.new_customers_created} new customers`);
        load();
        setImportBusy(false);
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
      toast.info("Receipt import started — running in background. You can stay on this page.");
      // Poll
      const poll = setInterval(async () => {
        try {
          const j = await api.get(`/import/job/${jobId}`);
          const data = j.data;
          if (data.status === "done") {
            clearInterval(poll);
            const res = data.result || {};
            toast.success(`✓ Imported ${res.imported || 0} receipts · ${res.skipped_existing || 0} skipped · ${res.new_customers_created || 0} new customers`);
            load();
            setImportBusy(false);
            if (fileRef.current) fileRef.current.value = "";
          } else if (data.status === "failed") {
            clearInterval(poll);
            toast.error(data.error || "Receipt import failed");
            setImportBusy(false);
            if (fileRef.current) fileRef.current.value = "";
          } else {
            // running — update toast
            toast.info(`${data.message || "Processing…"} (${data.progress || 0}%)`, { id: "rcp-import-progress" });
          }
        } catch (err) {
          clearInterval(poll);
          toast.error(formatApiError(err));
          setImportBusy(false);
        }
      }, 2500);
    } catch (err) {
      toast.error(formatApiError(err));
      setImportBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteReceipt = async (r) => {
    if (!window.confirm(`Delete receipt ${r.receipt_no}? Linked invoices will be rolled back.`)) return;
    try { await api.delete(`/receipts/${r.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const callWA = (phone, name, due) => {
    const text = `Namaste ${name}, aapka Singh Offset par ₹${Math.round(due).toLocaleString("en-IN")} outstanding hai. Kab tak clear karenge? Dhanyawad.`;
    const cleanPhone = (phone || "").replace(/\D/g, "");
    const p = cleanPhone.startsWith("91") ? cleanPhone : `91${cleanPhone}`;
    window.open(`https://wa.me/${p}?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (loading) return <div className="p-8 text-[#475569]">Loading payment dashboard…</div>;
  if (!data) return <div className="p-8 text-red-700">Failed to load.</div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="payment-dashboard">
      <PageHeader
        title="Payment Dashboard"
        subtitle="Outstanding · Overdue · Ageing · Debtor Summary · Busy Receipt Import"
        action={
          <div className="flex gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept=".xlsx" onChange={handleBusyUpload} className="hidden" data-testid="busy-file-input"/>
            <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={importBusy} data-testid="import-busy-btn">
              <Upload size={14}/> {importBusy ? "Importing…" : "Import Busy Excel"}
            </Button>
            <Button onClick={() => setAddOpen(true)} data-testid="add-receipt-btn"><Plus size={14}/> Add Receipt</Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="kpi-cards">
        <KPICard icon={Wallet} label="Outstanding" value={inr(data.kpi.outstanding)} tone="border-amber-500" testid="kpi-outstanding"/>
        <KPICard icon={AlertTriangle} label="Overdue (>15d)" value={inr(data.kpi.overdue)} tone="border-red-500" testid="kpi-overdue"/>
        <KPICard icon={TrendingDown} label="Advance Paid" value={inr(data.kpi.advance_paid)} tone="border-green-500" testid="kpi-advance"/>
        <KPICard icon={Users} label="Debtor Count" value={data.kpi.debtor_count} tone="border-blue-500" testid="kpi-debtor-count"/>
      </div>

      {/* Ageing Summary */}
      <div className="card-flat p-4 mb-6" data-testid="ageing-summary">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-2">Ageing Summary</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {k: "1-15", label: "1-15 Days", color: "green"},
            {k: "16-30", label: "16-30 Days", color: "amber"},
            {k: "31-45", label: "31-45 Days", color: "orange"},
            {k: ">45", label: ">45 Days", color: "red"},
          ].map((b) => (
            <div key={b.k} className={`p-3 rounded border-l-4 border-${b.color}-500 bg-${b.color}-50`} data-testid={`ageing-${b.k}`}>
              <div className={`text-[10px] uppercase tracking-wider font-bold text-${b.color}-700`}>{b.label}</div>
              <div className="font-mono-num font-bold text-lg">{inr(data.ageing[b.k] || 0)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Debtors Table */}
      <div className="card-flat p-4 mb-6">
        <h3 className="font-display font-bold text-base mb-3">Debtor Summary · {data.debtors.length} customers</h3>
        {data.debtors.length === 0 ? (
          <EmptyState icon={Wallet} title="No pending dues" hint="All invoices paid up!"/>
        ) : (
          <div className="overflow-x-auto" data-testid="debtors-table">
            <table className="w-full text-xs">
              <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-wider text-[#475569]">
                <tr>
                  <th className="text-left p-2">Customer</th>
                  <th className="text-right p-2">Total Due</th>
                  <th className="text-right p-2">Overdue</th>
                  <th className="text-center p-2">Invoices</th>
                  <th className="text-center p-2">Oldest (days)</th>
                  <th className="text-center p-2">Last Pay</th>
                  <th className="text-center p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.debtors.slice(0, 50).map((d) => {
                  const cust = customers.find((c) => c.id === d.customer_id);
                  return (
                    <tr key={d.customer_id} className="border-t border-[#E5E7EB] hover:bg-[#FEF2F2]/30" data-testid={`debtor-${d.customer_id}`}>
                      <td className="p-2 font-semibold">{d.customer_name}</td>
                      <td className="p-2 text-right font-mono-num font-bold">{inr(d.total_due)}</td>
                      <td className="p-2 text-right font-mono-num text-red-700">{d.total_overdue > 0 ? inr(d.total_overdue) : "—"}</td>
                      <td className="p-2 text-center font-mono-num">{d.invoice_count}</td>
                      <td className={`p-2 text-center font-mono-num font-bold ${d.oldest_due_days > 45 ? "text-red-700" : d.oldest_due_days > 15 ? "text-amber-700" : ""}`}>{d.oldest_due_days}</td>
                      <td className="p-2 text-center text-[10px]">{d.last_payment_date || "—"}</td>
                      <td className="p-2">
                        <div className="flex gap-1 justify-center">
                          {cust?.phone && <a href={`tel:${cust.phone}`} className="p-1 text-[#C1121F] hover:bg-[#FEF2F2] rounded"><Phone size={12}/></a>}
                          {cust?.phone && <button onClick={() => callWA(cust.phone, d.customer_name, d.total_due)} className="p-1 text-green-700 hover:bg-green-50 rounded"><MessageCircle size={12}/></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Receipts */}
      <div className="card-flat p-4">
        <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
          <ReceiptIcon size={16} className="text-[#C1121F]"/> This Month's Receipts · {receiptsList.length}
        </h3>
        {receiptsList.length === 0 ? (
          <div className="text-sm text-[#94A3B8] text-center py-4">No receipts this month.</div>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto" data-testid="receipts-list">
            <table className="w-full text-xs">
              <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-wider text-[#475569] sticky top-0">
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Receipt#</th>
                  <th className="text-left p-2">Customer</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-left p-2">Bank/Mode</th>
                  <th className="text-center p-2">Source</th>
                  {user?.role === "admin" && <th className="text-center p-2">Action</th>}
                </tr>
              </thead>
              <tbody>
                {receiptsList.slice(0, 100).map((r) => (
                  <tr key={r.id} className="border-t border-[#E5E7EB]" data-testid={`receipt-${r.receipt_no}`}>
                    <td className="p-2 font-mono-num">{r.receipt_date}</td>
                    <td className="p-2 font-mono-num font-semibold">{r.receipt_no}</td>
                    <td className="p-2">{r.customer_name}</td>
                    <td className="p-2 text-right font-mono-num font-bold">{inr(r.amount)}</td>
                    <td className="p-2 text-[10px] text-[#475569]">{r.bank_account}</td>
                    <td className="p-2 text-center">
                      <span className={`badge text-[10px] ${r.imported_from === "busy" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>{r.imported_from}</span>
                    </td>
                    {user?.role === "admin" && (
                      <td className="p-2 text-center">
                        <button onClick={() => deleteReceipt(r)} className="text-[#94A3B8] hover:text-red-600"><Trash2 size={12}/></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Receipt">
        <form onSubmit={addReceipt} className="space-y-3">
          <Field label="Customer" required>
            <Typeahead
              loadOptions={searchCustomersAsync}
              value={form.customer_id}
              selectedLabel={selectedCustomerLabel}
              onChange={(id, opt) => { setForm({...form, customer_id: id}); setSelectedCustomerLabel(opt?.name || ""); }}
              placeholder="Type to search customer…"
              required
              testid="receipt-customer-select"
              renderMeta={(c) => `${c.type || ""} ${c.phone ? "· " + c.phone : ""} ${c.city ? "· " + c.city : ""}`}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (₹)" required>
              <input data-testid="receipt-amount-input" required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="input-base font-mono-num"/>
            </Field>
            <Field label="Date" required>
              <input data-testid="receipt-date-input" required type="date" value={form.receipt_date} onChange={(e) => setForm({...form, receipt_date: e.target.value})} className="input-base"/>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Voucher No. (optional)">
              <input data-testid="receipt-voucher-input" value={form.voucher_no} onChange={(e) => setForm({...form, voucher_no: e.target.value})} className="input-base" placeholder="e.g. 1001"/>
            </Field>
            <Field label="Method">
              <select value={form.method} onChange={(e) => setForm({...form, method: e.target.value})} className="input-base">
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </Field>
          </div>
          <Field label="Bank/Account">
            <input value={form.bank_account} onChange={(e) => setForm({...form, bank_account: e.target.value})} className="input-base" placeholder="HDFC CA / Cash / GPay"/>
          </Field>
          <Field label="Note">
            <textarea value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} rows={2} className="input-base"/>
          </Field>
          <div className="p-2 bg-blue-50 border border-blue-200 rounded text-[11px] text-blue-800">
            💡 Amount will auto-allocate to oldest unpaid invoices (FIFO).
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-receipt-btn">Save Receipt</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
